import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { type FunctionalForm, useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { Validator } from '@adornis/validation/validation.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { addYears } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { getBillingGroup } from '../../../billingPositions/api/effectiveBillingPosition.js';
import {
  BillingPositionPriceInput,
  formatBillingPositionPrice,
} from '../../../billingPositions/client/BillingPositionPriceInput.js';
import { translateBillingRhythm } from '../../../billingPositions/entities/BillingPosition.js';
import { BillingPositionOverride } from '../../../billingPositions/entities/BillingPositionOverride.js';
import { SgFormGridEntry } from '../../../client/components/SgFormGridEntry.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import { Group } from '../../../groups/entities/Group.js';
import { Permission, Role } from '../../../permissions/roles.js';
import { getSchoolYear, getSchoolYearIntervalDatesFromDate } from '../../../schoolyear/getSchoolYear.js';
import { describeMandateRuntimeChanges } from '../../../sepa/client/describeMandateRuntimeChanges.js';
import { Person } from '../../../users/entities/person.js';
import { UserListAPI } from '../../../users/list/api/UserListAPI.js';
import { BookingAPI } from '../../api/BookingAPI.js';
import type { BookingHashIngnoreKey } from '../../api/bookingHash.js';
import { Booking } from '../../entities/Booking.js';
import { ClassBooking, Grade } from '../../entities/ClassBooking.js';
import { DonationBooking } from '../../entities/DonationBooking.js';
import { EmployeeBooking } from '../../entities/EmployeeBooking.js';
import type { GroupedBooking, GroupedBookingBooking, GroupedBookingPerson } from './useGroupedBookings.js';

// light DOM on purpose: Collapse lifts the label's links above its toggle with a selector that can't enter a shadow root
const SGMassBookingCardLabel = (props: { booking: GroupedBooking }) => {
  const newBooking = !props.booking.bookings.at(0)?._id;
  return html`
    <div class="flex flex-row grow gap-2 justify-between items-center">
      <span class="font-semibold">${props.booking.label}</span>
      <span class="badge ${newBooking ? 'badge-primary' : props.booking.active ? 'badge-success' : 'badge-warning'}">
        ${newBooking ? 'Neu' : props.booking.active ? 'aktiv' : 'inaktiv'}
      </span>
    </div>
  `;
};

const SchoolYearQuickSelection = stateful((props: { form: FunctionalForm<GroupedBookingBooking> }) => {
  /**
   * The five offered school years, anchored on today's calendar day rather than on `new Date()`.
   * Both end up in the same bucket, but starting from a canonical day keeps the whole chain on the
   * canonical grid instead of carrying a time-of-day around until something normalizes it.
   */
  const schoolYearOptions = Array.from({ length: 5 }, (_, i) => addYears(todayAsCalendarDay(), i - 2));

  return html`
    <div class="flex flex-row gap-2 items-center">
      <span class="text-sm font-semibold text-base-content/70">Schnellauswahl:</span>
      ${repeat(
        schoolYearOptions,
        date => formatCalendarDay(date, 'yyyy-MM-dd'),
        (date, index) => {
          const schoolYear = getSchoolYearIntervalDatesFromDate(date);
          // canonical calendar days compare as plain instants
          const isSelected =
            !!props.form.document.startDate &&
            !!schoolYear.start &&
            props.form.document.startDate.getTime() === schoolYear.start.getTime();

          return html`<div
            class="badge cursor-pointer hover:scale-105 transition-all ${
              isSelected ? 'badge-primary' : index < 2 ? 'badge-error badge-outline' : 'badge-outline'
            }"
            @click=${() => {
              props.form.document.startDate = schoolYear.start;
              props.form.document.endDate = schoolYear.end;
            }}
          >
            ${getSchoolYear({ date })}
          </div>`;
        },
      )}
    </div>
  `;
});

/**
 * BaseQL drops any request carrying more than 50 Mongo ids, so a mass edit is sent in slices.
 * Ponytail: the slices are not atomic across each other - a failing slice leaves the earlier ones
 * applied. Acceptable here, the user simply redoes the edit; a server-side bulk operation would fix it.
 */
const chunk = <T>(items: T[], size: number) =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, (i + 1) * size));

async function handleDeletion(bookings: GroupedBookingBooking[]): Promise<void> {
  const hardDelete = await confirmDialog(
    'Buchungen sollten im Normalfall nicht gelöscht werden, sondern nur beendet werden. Wollen Sie die Buchungen wirklich vollständig löschen oder lieber das Enddatum auf "Heute" setzen?',
    { highlightDecline: true, declineText: 'Enddatum auf Heute setzen', acceptText: 'Vollständig löschen' },
  );

  if (!hardDelete) {
    if (
      !(await confirmDialog(`Sind Sie sicher, dass Sie alle ${bookings.length} Buchungen wirklich beenden möchten?`, {
        highlightDecline: true,
        declineText: 'Abbrechen',
        acceptText: 'Buchungen beenden',
      }))
    ) {
      return;
    }

    await guardDialog(
      async () => {
        for (const booking of bookings) booking.endDate = todayAsCalendarDay();
        for (const bookingChunk of chunk(bookings as Booking[], 10)) {
          await BookingAPI.upsert(bookingChunk)({ upserted: 1 });
        }
      },
      {
        success: () => {
          void SgSnackbar.show('Buchungen beendet');
        },
      },
    );

    return;
  }

  if (
    !(await confirmDialog(
      `Sind Sie sicher, dass Sie alle ${bookings.length} Buchungen wirklich vollständig löschen möchten? Dies bedeutet auch, dass diese aus dem Verlauf gelöscht werden!`,
      { highlightDecline: true, declineText: 'Abbrechen', acceptText: 'Buchungen löschen' },
    ))
  ) {
    return;
  }

  await guardDialog(
    async () => {
      for (const ids of chunk(
        bookings.map(b => b._id),
        40,
      )) {
        await BookingAPI.remove(ids)();
      }
    },
    {
      success: () => {
        void SgSnackbar.show('Buchungen gelöscht');
      },
    },
  );
}

function parseBookingChange(
  booking: Booking | ClassBooking | DonationBooking,
  toClass: string,
  preserveOriginal?: {
    userID?: string;
    grade?: string;
    classIdentifier?: string;
    billingPosition?: BillingPositionOverride | null;
  },
) {
  let newBooking: Booking | ClassBooking | DonationBooking = booking;

  switch (toClass) {
    case Booking._class:
      // @ts-expect-error is only on subclass
      booking.grade = null;
      // @ts-expect-error is only on subclass
      booking.classIdentifier = null;
      newBooking = new Booking({ ...booking.toJSON() });
      break;
    case ClassBooking._class:
      newBooking = new ClassBooking({ ...booking.toJSON() });
      break;
    case DonationBooking._class:
      // @ts-expect-error is only on subclass
      booking.grade = null;
      // @ts-expect-error is only on subclass
      booking.classIdentifier = null;
      newBooking = new DonationBooking({ ...booking.toJSON() });
      break;
    case EmployeeBooking._class:
      // @ts-expect-error is only on subclass
      booking.grade = null;
      // @ts-expect-error is only on subclass
      booking.classIdentifier = null;
      newBooking = new EmployeeBooking({ ...booking.toJSON() });
      break;
    default:
      throw new Error('Unknown booking class');
  }

  if (preserveOriginal) {
    if (preserveOriginal.userID !== undefined) newBooking.userID = preserveOriginal.userID;
    // @ts-expect-error is only on subclass
    if (preserveOriginal.grade !== undefined) newBooking.grade = preserveOriginal.grade;
    // @ts-expect-error is only on subclass
    if (preserveOriginal.classIdentifier !== undefined) newBooking.classIdentifier = preserveOriginal.classIdentifier;
    if (preserveOriginal.billingPosition !== undefined) newBooking.billingPosition = preserveOriginal.billingPosition;
  }

  return newBooking;
}

function getInfoString(booking: Booking | ClassBooking | DonationBooking) {
  switch (booking._class) {
    case ClassBooking._class:
      return `Klassenbuchungen verbinden Personen mit einer Schule und der jeweiligen Klasse im aktuellen Schuljahr. Zum Beispiel SuS und Klassenlehrer.`;
    case DonationBooking._class:
      return `Spendenbuchungen verbinden Spender direkt mit einer Schule um Spenden ohne SuS zu erfassen.`;
    case EmployeeBooking._class:
      return `Personalbuchungen stellen den aktuellen Vertrag dar. Sie können daher nicht direkt bearbeitet werden, sondern werden nur über die jeweiligen Werte des aktiven Vertrags verändert. Hiemit können zum Beispiel Abrechnungspositionen an das Personal übertragen werden.`;
    default:
      return `Buchungen verbinden beliebige Personen mit einer beliebigen Gruppe und stellen die Basisfunktion des Buchungssystem dar.`;
  }
}

/**
 * What the booking pays: its billing group's Abrechnungsposition, and on top of it - when
 * switched on - the booking's own price. The rhythm is always the group's.
 */
const BillingPositionOverrideEdit = stateful(
  (props: { form: FunctionalForm<GroupedBookingBooking>; groups: GroupedBookingBooking['groups'] }) => {
    const billingGroup = getBillingGroup(props.form.document, (props.groups ?? []).filter(isNotNullish));
    const groupPosition = billingGroup?.billingPosition;
    const override = props.form.document.billingPosition;

    if (!groupPosition) {
      const carriers = (props.groups ?? []).filter(group => group?.billingPosition);

      return html`
        <div class="text-sm text-base-content/70">
          ${
            carriers.length > 1
              ? 'Die Buchung liegt in mehreren Gruppen mit Abrechnungsposition und wird deshalb nicht abgerechnet. Bitte eine der Gruppen entfernen.'
              : 'Keine der Gruppen hat eine Abrechnungsposition - die Buchung wird nicht abgerechnet.'
          }
        </div>
      `;
    }

    return html`
      <div class="flex flex-col gap-3">
        <div class="card p-4 flex grow bg-base-200">
          <div class="flex flex-row gap-4 items-center justify-between">
            <span class="font-semibold">${groupPosition.name} (${billingGroup.name})</span>
            <div class="flex flex-row gap-2 items-center">
              <span class="font-medium">${formatBillingPositionPrice(groupPosition.priceInCents)}</span>
              <span class="badge badge-primary">
                ${groupPosition.rhythm ? translateBillingRhythm(groupPosition.rhythm) : 'Selbstauswahl'}
              </span>
            </div>
          </div>
        </div>

        <div class="flex flex-row gap-3 items-center">
          ${ToggleInput({
            value: !!override,
            onChanged: value => {
              props.form.document.billingPosition = value
                ? new BillingPositionOverride({ priceInCents: groupPosition.priceInCents ?? null })
                : null;
            },
          })}
          <span>Diese Buchung zahlt abweichend von der Gruppe</span>
        </div>

        ${
          override
            ? html`
                <div class="form-control">
                  <label class="label py-1"
                    ><span class="label-text text-sm font-semibold text-base-content/70">Betrag</span></label
                  >
                  ${BillingPositionPriceInput(props.form.field('billingPosition', 'priceInCents'))}
                </div>
              `
            : nothing
        }
      </div>
    `;
  },
);

const ReassignBookingUserDialog = stateful(
  (props: {
    bookingID: string;
    currentPerson: Maybe<GroupedBookingPerson>;
    resolve: (value: unknown) => void;
    reject: () => void;
  }) => {
    const [newUserID, setNewUserID] = useState<Maybe<string>>(null);
    // a filter rebuilt per render carries a fresh _id and refetches the picker
    const [filter] = useState(new AdornisFilter({}));
    const name = props.currentPerson?.profilDaten?.stammdaten;

    const handleAccept = async () => {
      if (!newUserID) return;
      await guardDialog(
        () => BookingAPI.reassignUser(props.bookingID, newUserID)({ endedMandates: 1, overlapping: 1 }),
        {
          success: result => {
            props.resolve(result);
            const message = result?.endedMandates
              ? `Person geändert, ${result.endedMandates} SEPA-Mandat(e) beendet`
              : 'Person geändert';
            void SgSnackbar.show(
              result?.overlapping
                ? `${message}. Achtung: Die Person hat jetzt doppelte Buchungen auf diesen Gruppen.`
                : message,
            );
          },
        },
      );
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'Person der Buchung ändern', close: props.reject, closeLabel: 'Dialog schließen' })}

        <p>
          Nur zur Fehlerbehebung: Die Buchung wird von
          <b>${name ? `${name.vorname} ${name.nachname}` : '---'} (${props.currentPerson?.debitorennummer ?? '---'})</b>
          auf die ausgewählte Person umgehängt. Rollen und Gruppen beider Personen werden neu berechnet.
        </p>
        <div class="alert alert-warning alert-soft">
          Offene SEPA-Mandate dieser Buchung, deren Kontoinhaber weder die neue Person noch ein Sorgeberechtigter oder
          Spender von ihr ist, werden zu gestern beendet.
        </div>

        ${EntityPickerInput({
          value: newUserID,
          class: Person,
          operation: UserListAPI.subscribe,
          filter,
          placeholder: 'Neue Person',
          placeholderMode: 'hide',
          renderString: entity =>
            `${entity.debitorennummer ?? ''} (${entity.profilDaten?.stammdaten?.vorname ?? ''} ${entity.profilDaten?.stammdaten?.nachname ?? ''})`,
          selectionSet: { _id: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } },
          onPicked: value => setNewUserID(value),
        })}

        <button class="btn btn-primary" ?disabled=${!newUserID} @click=${handleAccept}>Übernehmen</button>
      </div>
    `;
  },
);

/**
 * Client validation for the fields this card actually edits. The entity validator is not usable
 * here: it validates `userID`, which the card never edits and which stays per booking as soon as
 * the persons are grouped away, and its messages are developer English rather than something a
 * user is meant to read.
 */
const MassBookingFormValidator = {
  groupIDs: (options: ValidationFunctionOptions<Maybe<string[]>, GroupedBookingBooking>) => {
    if (!options.value?.length)
      throw new ValidationError('Die Buchung muss mindestens einer Gruppe zugeordnet sein.', {
        key: options.key,
        path: options.path,
      });
  },
  endDate: (options: ValidationFunctionOptions<Maybe<Date>, GroupedBookingBooking>) => {
    const startDate = options.target.startDate;
    // canonical calendar days compare as plain instants; end === start is a one day booking
    if (options.value && startDate && options.value.getTime() < startDate.getTime())
      throw new ValidationError('Das Enddatum darf nicht vor dem Startdatum liegen.', {
        key: options.key,
        path: options.path,
      });
  },
} satisfies Validator;

export const SGMassBookingCard = stateful(
  (props: {
    booking: GroupedBooking;
    ignoreGroupingKeys: BookingHashIngnoreKey[];
    forceOpen?: boolean;
    forceClose?: boolean;
    savedCallback?: () => void;
    groupSize?: number;
  }) => {
    const [firstBooking, setFirstBooking] = useState(props.booking.bookings[0]);
    const user = useObservable(() => currentUser(), []);

    const ignoredKeys = {
      userID: props.ignoreGroupingKeys.includes('userID'),
      grade: props.ignoreGroupingKeys.includes('grade'),
      classIdentifier: props.ignoreGroupingKeys.includes('classIdentifier'),
      billingPosition: props.ignoreGroupingKeys.includes('billingPosition'),
    };

    const form = useForm(firstBooking ?? new Booking({}), { validator: MassBookingFormValidator }, [firstBooking]);
    // contract bookings are generated, and on saved bookings Typ and Gruppen were a frequent source of mistakes
    const typeAndGroupsLocked =
      form.document._class === EmployeeBooking._class ||
      (!!firstBooking?._id && !user?.hasPermission(Permission.EDIT_BOOKING_TYPE_AND_GROUPS, ANY_CONTEXT));

    // the deps re-sync the open state whenever the editor forces all cards open/closed, while the user can still
    // toggle a single card in between
    const defaultOpen = Boolean(
      props.forceOpen
        ? true
        : props.forceClose
          ? false
          : props.groupSize && props.groupSize > 1
            ? false
            : props.booking.active,
    );
    const [open, setOpen] = useState(defaultOpen, [defaultOpen]);

    const handleSave = async () => {
      const error = await form.validate();

      if (error) {
        await openValidationErrorDialog({ error, hardValidate: true });
        return;
      }

      // write all changes of the first booking to all bookings
      for (let i = 0; i < props.booking.bookings.length; i++) {
        const booking = props.booking.bookings[i];
        if (!booking) {
          continue;
        }

        const preserveOriginal: {
          userID?: string;
          grade?: string;
          classIdentifier?: string;
          billingPosition?: BillingPositionOverride | null;
        } = {};

        if (ignoredKeys.userID) {
          preserveOriginal.userID = booking.userID ?? undefined;
        }
        if (ignoredKeys.grade) {
          // @ts-expect-error is only on subclass
          preserveOriginal.grade = booking.grade ?? undefined;
        }
        if (ignoredKeys.classIdentifier) {
          // @ts-expect-error is only on subclass
          preserveOriginal.classIdentifier = booking.classIdentifier ?? undefined;
        }
        if (ignoredKeys.billingPosition) {
          preserveOriginal.billingPosition =
            (booking.billingPosition as BillingPositionOverride | null | undefined) ?? null;
        }

        // convert to the target class *before* writing the form values onto it. Subclass fields only exist on an
        // instance of that subclass, so assigning them beforehand only creates a plain JS property which the
        // `toJSON()` inside parseBookingChange drops again (this is what loses grade/classIdentifier when a
        // ClassBooking gets created out of a freshly instantiated `new Booking({})`).
        const targetBooking = parseBookingChange(booking as Booking, form.document._class, preserveOriginal);

        targetBooking.groupIDs = form.document.groupIDs;
        // the form values are already canonical calendar days (end = last included day at 00:00Z)
        targetBooking.startDate = form.document.startDate ?? null;
        targetBooking.endDate = form.document.endDate ?? null;

        if (!ignoredKeys.grade) {
          // @ts-expect-error is only on subclass
          targetBooking.grade = form.document.grade?.trim() || null;
        }

        if (!ignoredKeys.classIdentifier) {
          // @ts-expect-error is only on subclass
          targetBooking.classIdentifier = form.document.classIdentifier?.trim() || null;
        }
        if (!ignoredKeys.billingPosition) {
          targetBooking.billingPosition = form.document.billingPosition
            ? new BillingPositionOverride({ ...form.document.billingPosition })
            : null;
        }

        props.booking.bookings[i] = targetBooking;
      }

      // a mandate runs only within its booking, so shortening a booking ends or moves its mandates too
      const mandateChanges = await describeMandateRuntimeChanges(props.booking.bookings as Booking[]);
      if (
        mandateChanges &&
        !(await confirmDialog(mandateChanges, {
          highlightDecline: true,
          acceptText: 'Speichern',
          declineText: 'Abbrechen',
        }))
      )
        return;

      await guardDialog(async () => {
        for (const bookingChunk of chunk(props.booking.bookings as Booking[], 10)) {
          await BookingAPI.upsert(bookingChunk)({ upserted: 1 });
        }
      });
      props.savedCallback?.();
    };

    const cardContent = html`
      <div class="flex flex-col gap-8">
        <div class="grid grid-cols-[1fr_4fr] gap-3">
          ${SgFormGridEntry({
            label: 'Typ',

            render: html`
              ${DropdownSelectionInput({
                value: form.document._class,
                selectables: [Booking._class, ClassBooking._class, DonationBooking._class],
                disabled: typeAndGroupsLocked,
                renderString: value => {
                  if (value === Booking._class) return 'Buchung';
                  if (value === ClassBooking._class) return 'Klassenbuchung';
                  if (value === DonationBooking._class) return 'Spendenbuchung';
                  if (value === EmployeeBooking._class) return 'Personalbuchung';
                  return value;
                },
                onPicked: value => {
                  if (!firstBooking || !value) return;
                  setFirstBooking(parseBookingChange(firstBooking as Booking, value));
                },
              })}
            `,
          })}
          <div></div>
          <p class="p-2">${getInfoString(form.document as Booking)}</p>
          <div class="divider col-span-2"></div>
          ${SgFormGridEntry({
            label: 'Gruppen',

            render: html`
              ${EntityPickerInput({
                ...form.field('groupIDs'),
                class: Group,
                operation: GroupsAPI.subscribeGroups,
                disabled: typeAndGroupsLocked,
                filter: new AdornisFilter({
                  searchFields: ['name'],
                  aspects: [new BooleanAspect({ customID: 'onlyInternal', value: true })],
                }),
                multi: true,
                selectionSet: { name: 1, _id: 1 },
              })}
            `,
          })}
          ${
            form.document._class === EmployeeBooking._class
              ? nothing
              : html`
                  <div></div>
                  ${SchoolYearQuickSelection({ form })}
                `
          }
          ${
            form.document._class === EmployeeBooking._class
              ? SgFormGridEntry({
                  label: 'Laufzeit',
                  render: html`
                    <div class="flex flex-row gap-2 items-center">
                      ${DateInput({
                        ...form.field('startDate'),
                        disabled: true,
                        clearable: true,
                        placeholder: 'von',
                        placeholderMode: 'prefix',
                      })}
                      <span>-</span>
                      ${DateInput({
                        ...form.field('endDate'),
                        clearable: true,
                        disabled: true,
                        placeholder: 'Offenes Ende',
                        placeholderMode: 'hide',
                      })}
                    </div>
                  `,
                })
              : SgFormGridEntry({
                  label: 'Laufzeit',
                  render: html`
                    <div class="flex flex-row gap-2 items-center">
                      ${DateInput({
                        ...form.field('startDate'),
                        clearable: true,
                        placeholder: 'von',
                        placeholderMode: 'prefix',
                      })}
                      <span>-</span>
                      ${DateInput({
                        ...form.field('endDate'),
                        clearable: true,
                        placeholder: 'Offenes Ende',
                        placeholderMode: 'hide',
                      })}
                    </div>
                  `,
                })
          }
          ${
            form.document._class === ClassBooking._class
              ? html`
                  ${
                    ignoredKeys.grade
                      ? nothing
                      : SgFormGridEntry({
                          label: 'Jahrgangsstufe',
                          render: html`
                            ${DropdownSelectionInput({
                              ...form.field('grade'),
                              selectables: Object.values(Grade),
                              placeholder: 'Keine Jahrgangsstufe ausgewählt',
                              placeholderMode: 'hide',
                            })}
                          `,
                        })
                  }
                  ${
                    ignoredKeys.classIdentifier
                      ? nothing
                      : SgFormGridEntry({
                          label: 'Klassenbezeichnung',
                          render: html`
                            ${TextInput({
                              ...form.field('classIdentifier'),
                              placeholder: 'Keine Klassenbezeichnung ausgewählt',
                              placeholderMode: 'hide',
                            })}
                          `,
                        })
                  }
                `
              : nothing
          }
          ${
            !firstBooking?._id
              ? SgFormGridEntry({
                  label: 'Abrechnung',
                  render: html` <p>Die Abrechnung kann erst auf erstellten Buchungen bearbeitet werden.</p> `,
                })
              : ignoredKeys.billingPosition
                ? nothing
                : SgFormGridEntry({
                    label: 'Abrechnung',
                    customLabelTopMargin: '1.6rem',
                    render: BillingPositionOverrideEdit({ form, groups: firstBooking.groups }),
                  })
          }
        </div>

        <div class="flex flex-row gap-2 items-center justify-end">
          ${
            user?.roles.some(r => r.name === Role.ADMIN) &&
            firstBooking?._id &&
            props.booking.bookings.length === 1 &&
            form.document._class !== EmployeeBooking._class
              ? html`
                  <button
                    class="btn btn-lg btn-outline"
                    @click=${() =>
                      showDialog({
                        content: (resolve, reject) =>
                          ReassignBookingUserDialog({
                            bookingID: firstBooking._id,
                            currentPerson: props.booking.users.find(u => u._id === firstBooking.userID),
                            resolve,
                            reject,
                          }),
                        modalBoxClasses: 'max-w-2xl',
                      }).catch(() => {})}
                  >
                    <span class="icon-[material-symbols--manage-accounts-rounded] size-5"></span> Person ändern
                  </button>
                `
              : nothing
          }
          ${
            user?.hasPermission(Permission.DELETE_BOOKINGS, ANY_CONTEXT) && firstBooking?._id
              ? html`
                  <button
                    class="btn btn-error btn-lg btn-outline"
                    @click=${async () => {
                      await handleDeletion(props.booking.bookings);
                    }}
                  >
                    <span class="icon-[material-symbols--delete-rounded] size-5"></span> Buchungen löschen
                  </button>
                `
              : nothing
          }
          <button class="btn btn-primary btn-lg" @click=${handleSave}>
            <span class="icon-[material-symbols--save-rounded] size-5"></span>
            ${firstBooking?._id ? 'Speichern' : 'Buchungen erstellen'}
          </button>
        </div>
      </div>
    `;

    return html`
      <!-- the collapse is the card; the wrapper only carries the anchor id -->
      <div id=${props.booking.hash}>
        ${Collapse({
          heading: SGMassBookingCardLabel({ booking: props.booking }),
          open,
          onToggle: setOpen,
          content: cardContent,
        })}
      </div>
    `;
  },
);
